import { React, PT } from '@gasbuddy/react';
import { Table } from 'semantic-ui-react';
import JSONTree from 'react-json-tree';

const theme = {
  scheme: 'monokai',
  author: 'wimer hazenberg (http://www.monokai.nl)',
  base00: '#272822',
  base01: '#383830',
  base02: '#49483e',
  base03: '#75715e',
  base04: '#a59f85',
  base05: '#f8f8f2',
  base06: '#f5f4f1',
  base07: '#f9f8f5',
  base08: '#f92672',
  base09: '#fd971f',
  base0A: '#f4bf75',
  base0B: '#a6e22e',
  base0C: '#a1efe4',
  base0D: '#66d9ef',
  base0E: '#ae81ff',
  base0F: '#cc6633',
};

function leftover(l) {
  const left = Object.assign({}, l);
  delete left.correlationid;
  delete left.c;
  delete left.level;
  delete left.message;
  delete left.label;
  delete left.ts;
  return left;
}

function pad(v, d) {
  const str = String(v);
  if (str.length < d) {
    return `${new Array((d - str.length) + 1).join('0')}${str}`;
  }
  return str;
}

function deltaT(offset) {
  const min = offset / (1000 * 60);
  const sign = offset >= 0 ? '+' : '-';
  return `${sign}${parseInt(min, 10)}:${pad(parseInt(offset / 1000, 10) % 60, 2)}.${pad(offset % 1000, 3)}`;
}

function groupSpans(logs) {
  const spans = {};
  logs.forEach((l) => {
    const spanId = l.span || l.sp;
    let endTs = l.ts;
    if (l.dur) {
      endTs += Math.floor(l.dur * 1000);
    }
    spans[spanId] = spans[spanId] || { sp: spanId, min: l.ts, max: l.ts, s: l.label };
    spans[spanId].min = Math.min(l.ts, spans[spanId].min);
    spans[spanId].max = Math.max(l.ts, spans[spanId].max);
    spans[spanId].max = Math.max(endTs, spans[spanId].max);
    spans[spanId].dur = spans[spanId].max - spans[spanId].min;
  });
  return Object.values(spans);
}

export class CorrelationIdTable extends React.Component {
  state = { spans: [] }

  shouldShowSpan = (log) => {
    const { spans } = this.state;
    return spans.length === 0 || spans.includes(log.span || log.sp);
  }

  toggleRow(sp) {
    const { spans } = this.state;
    if (spans.includes(sp)) {
      this.setState({
        spans: spans.filter(span => span !== sp),
      });
    } else {
      this.setState({
        spans: [...spans, sp],
      });
    }
  }

  render() {
    const { logs } = this.props;
    if (!logs) {
      return null;
    }
    if (!logs.length) {
      return (<i>No records found</i>);
    }

    const minTs = logs.reduce((p, c) => Math.min(p, c.ts), logs[0].ts);
    const maxTs = logs
      .reduce((p, c) => Math.max(p, c.dur ? Math.round((c.dur * 1000) + c.ts) : c.ts), logs[0].ts);
    const spans = groupSpans(logs);
    const { spans: stateSpans } = this.state;

    return (
      <div>
        <Table basic="very">
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell width={2}>Service</Table.HeaderCell>
              <Table.HeaderCell width={1}>Span</Table.HeaderCell>
              <Table.HeaderCell style={{
                borderLeft: '1px solid gray',
                borderRight: '1px solid gray',
                borderRadius: 0,
                fontSize: '.8em',
              }}
              >
                0ms
                <div style={{ float: 'right', paddingRight: 5 }}>
                  {maxTs - minTs}
ms
                </div>
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {spans.map(s => (
              <Table.Row key={s.sp} onClick={(e) => { e.stopPropagation(); this.toggleRow(s.sp); }}>
                <Table.Cell>{s.s}</Table.Cell>
                <Table.Cell>{s.sp}</Table.Cell>
                <Table.Cell style={{ position: 'relative' }}>
                  <div style={{
                    backgroundColor: (stateSpans.length && !this.shouldShowSpan(s)) ? 'aliceblue' : 'lightblue',
                    position: 'absolute',
                    verticalAlign: 'middle',
                    padding: 10,
                    whiteSpace: 'nowrap',
                    left: `${parseInt(((s.min - minTs) * 100) / (maxTs - minTs), 10)}%`,
                    right: `${Math.max(0, 100 - parseInt(((s.max - minTs) * 100) / (maxTs - minTs), 10))}%`,
                    top: 0,
                    bottom: 0,
                  }}
                  >
                    <small>
                      {s.min - minTs}
ms -
                      {' '}
                      {s.max - minTs}
ms
                    </small>
                  </div>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
        <Table selectable>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell width={1}>Level</Table.HeaderCell>
              <Table.HeaderCell width={2}>Service</Table.HeaderCell>
              <Table.HeaderCell textAlign="center" width={2}>ΔT</Table.HeaderCell>
              <Table.HeaderCell>Message</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {logs.filter(this.shouldShowSpan).map(l => (
              <Table.Row key={`${l.host}.${l.ctr}`}>
                <Table.Cell>{l.level}</Table.Cell>
                <Table.Cell>{l.label}</Table.Cell>
                <Table.Cell textAlign="center">{deltaT(l.ts - minTs)}</Table.Cell>
                <Table.Cell>
                  {l.message === 'req' ? `${l.m} ${l.url}` : l.message}
                  <br />
                  <JSONTree
                    invertTheme
                    hideRoot
                    shouldExpandNode={() => false}
                    data={{ metadata: leftover(l) }}
                    theme={theme}
                  />
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </div>
    );
  }
}

CorrelationIdTable.propTypes = {
  logs: PT.arrayOf(PT.shape({
    level: PT.string.isRequired,
  })),
};

CorrelationIdTable.defaultProps = {
  logs: [],
};
